// src/pages/providers/5_patientOverview/config/patientOverviewConfig.js

// Define the base/default configuration
const defaultConfig = {
  moduleOrder: ['clinicalInterview', 'semiStructuredInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
  modules: {
    clinicalInterview: {
      title: "Clinical Interview",
      circleNumber: 1,
      alwaysVisible: true,
      disableWhenNotAdhd: false
    },
    semiStructuredInterview: {
      title: "Semi-Structured Interview",
      circleNumber: 2,
      alwaysVisible: false,
      disableWhenNotAdhd: true
    },
    diagnosticDecision: {
      title: "Diagnostic Decision",
      circleNumber: 3,
      alwaysVisible: true,
      disableWhenNotAdhd: false
    },
    postAssessmentDiscussion: {
      title: "Post-Assessment Discussion",
      circleNumber: 4,
      alwaysVisible: false,
      disableWhenNotAdhd: true
    }
  }
};

// SLaM specific configuration (DEPRECATED static map — the live overview uses
// derivePatientOverviewConfig). Default overview order is SSI → CI → DD → PAD
// (the canonical "decide then discuss" order). derivePatientOverviewConfig flips
// to SSI → CI → PAD → DD only for Path 1 (ciFinishPath === 'padInAppointment'),
// where the Lead runs the PAD in the same appointment straight after CI.
//
// Since Oct 2026 new SLaM / 3 Borough patients are stamped with the
// one-clinician CI-first preset and never reach this map; the SSI-first layout
// below is only reached by legacy callers for historical, unstamped patients.
const slamConfig = {
  moduleOrder: ['semiStructuredInterview', 'clinicalInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
  modules: {
    clinicalInterview: {
      title: "Clinical Interview",
      circleNumber: 2, // Second in SLaM's order
      alwaysVisible: true,
      disableWhenNotAdhd: false
    },
    semiStructuredInterview: {
      title: "Semi-Structured Interview",
      circleNumber: 1, // First in SLaM's order
      alwaysVisible: true, // Never disabled since it's done first
      disableWhenNotAdhd: false
    },
    diagnosticDecision: {
      title: "Diagnostic Decision",
      circleNumber: 3,
      alwaysVisible: true,
      disableWhenNotAdhd: false
    },
    postAssessmentDiscussion: {
      title: "Post-Assessment Discussion",
      circleNumber: 4,
      alwaysVisible: true, // Never disabled for SLaM
      disableWhenNotAdhd: false
    }
  }
};

// MDT Org configuration - same as default but with custom DD title
const mdtReviewConfig = {
  ...defaultConfig,
  modules: {
    ...defaultConfig.modules,
    diagnosticDecision: {
      ...defaultConfig.modules.diagnosticDecision,
      title: "Diagnostic Decision (MDT Review)"
    }
  }
};

// Export the configurations for different organizations
export const overviewConfig = {
  // Testing / Demos
  default: defaultConfig,
  "ThinkDivergent": defaultConfig,   
  "ThinkDivergent Admin": defaultConfig,
  // Customers - most use default, some have custom configs
  "Phoenix Mental Health Services": defaultConfig,    
  "Well Life Clinic": mdtReviewConfig, // Custom DD title: "Diagnostic Decision (MDT Review)"
  "ADHD Clinical UK": mdtReviewConfig, // Same MDT Review title as Well Life Clinic
  "Lancaster Medical Practice": mdtReviewConfig, // Custom DD title: "Diagnostic Decision (MDT Review)" 
  "MDT Org": mdtReviewConfig, // Custom DD title: "Diagnostic Decision (MDT Review)" 
  "ADHD and Autism Associates": mdtReviewConfig, // Custom DD title: "Diagnostic Decision (MDT Review)"
  "The Spectrum Mind Clinic": defaultConfig,
  "SLaM": slamConfig, // Custom module order: SSI → CI → DD → PAD
  "3 Borough Adult ADHD and Autism Service": slamConfig, // Custom module order: SSI → CI → DD → PAD
  "Psychological Consultations": defaultConfig,
  "The Owl Centre": mdtReviewConfig,  // Custom DD title: "Diagnostic Decision (MDT Review)"
  "Demo Org": mdtReviewConfig,
};

/**
 * @deprecated Use derivePatientOverviewConfig(workflowConfig) instead.
 * This map-based lookup is retained for any legacy callers that only have an
 * org name string.  New code should pass the resolved workflow config object.
 */
export const getPatientOverviewConfig = (organizationName) => {
  return overviewConfig[organizationName] || overviewConfig.default;
};

/**
 * Derive the Patient Overview module config from the centralised workflow
 * config object (returned by useWorkflowConfig or resolveWorkflowConfig).
 *
 * This replaces the static org-name map above with a dynamic derivation so
 * that patients with a stamped workflowConfig (or legacy patients whose
 * config is derived from their org name) both get the correct layout without
 * any org-name string matching at the call site.
 *
 * The returned shape is identical to the map-based configs (moduleOrder,
 * modules with title/circleNumber/alwaysVisible/disableWhenNotAdhd) so the
 * consuming component (PatientOverview.js) needs no structural changes.
 *
 * @param {Object} wf - Resolved workflow config with computed helpers
 *   (isSSIFirst, isMDTReview, isSSIAlwaysExpected, ciFinishPath, etc.)
 * @param {Object} [opts]
 * @param {boolean} [opts.padRedone] - True once the PAD has been redone for a
 *   changed Diagnostic Decision. When a Path-1 PAD is redone, the reopened
 *   discussion genuinely follows the formal DD, so the overview reverts to the
 *   canonical DD-third / PAD-fourth order (the PAD-before-DD flip is suppressed).
 * @returns {Object} Overview config in the same shape as defaultConfig/slamConfig
 */
export const derivePatientOverviewConfig = (wf, opts = {}) => {
  if (!wf) return defaultConfig;

  // SSI-first orgs (SLaM): SSI shown first, all modules always visible.
  //
  // Ordering is DD-before-PAD by DEFAULT — this is the canonical clinical order
  // (decide, then discuss) and the prior behaviour for every existing in-flight
  // patient whose ciFinishPath is not yet set, as well as Path 2 (deferPad,
  // where the Lead ends the appointment after CI and does DD before inviting the
  // patient back for PAD). We flip to PAD-before-DD ONLY for Path 1
  // (padInAppointment), where the Lead runs the PAD in the same appointment
  // straight after CI and completes the formal DD afterwards. Keeping DD-first
  // as the default avoids re-ordering the overview for existing patients.
  //
  // A REDO overrides the flip: once the Path-1 PAD has been redone for a changed
  // DD (opts.padRedone), the fresh discussion genuinely follows the finalised DD,
  // so we revert to the canonical DD-third / PAD-fourth order. The flip is tied to
  // the redo action, not the mismatch — a mismatch "left as is" performs no redo,
  // so PAD stays third (matching what actually happened with the patient).
  if (wf.isSSIFirst) {
    const padInAppointment = wf.ciFinishPath === 'padInAppointment' && !opts.padRedone;
    const ddTitle = wf.isMDTReview ? "Diagnostic Decision (MDT Review)" : "Diagnostic Decision";

    if (!padInAppointment) {
      return {
        moduleOrder: ['semiStructuredInterview', 'clinicalInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
        modules: {
          clinicalInterview: {
            title: "Clinical Interview",
            circleNumber: 2,
            alwaysVisible: true,
            disableWhenNotAdhd: false,
          },
          semiStructuredInterview: {
            title: "Semi-Structured Interview",
            circleNumber: 1,
            alwaysVisible: true,
            disableWhenNotAdhd: false,
          },
          diagnosticDecision: {
            title: ddTitle,
            circleNumber: 3,
            alwaysVisible: true,
            disableWhenNotAdhd: false,
          },
          postAssessmentDiscussion: {
            title: "Post-Assessment Discussion",
            circleNumber: 4,
            alwaysVisible: true,
            disableWhenNotAdhd: false,
          },
        },
      };
    }

    return {
      moduleOrder: ['semiStructuredInterview', 'clinicalInterview', 'postAssessmentDiscussion', 'diagnosticDecision'],
      modules: {
        clinicalInterview: {
          title: "Clinical Interview",
          circleNumber: 2,
          alwaysVisible: true,
          disableWhenNotAdhd: false,
        },
        semiStructuredInterview: {
          title: "Semi-Structured Interview",
          circleNumber: 1,
          alwaysVisible: true,
          disableWhenNotAdhd: false,
        },
        postAssessmentDiscussion: {
          title: "Post-Assessment Discussion",
          circleNumber: 3,
          alwaysVisible: true,
          disableWhenNotAdhd: false,
        },
        diagnosticDecision: {
          title: ddTitle,
          circleNumber: 4,
          alwaysVisible: true,
          disableWhenNotAdhd: false,
        },
      },
    };
  }

  // Standard CI-first order — may have MDT Review DD title or always-visible SSI
  const ddTitle = wf.isMDTReview ? "Diagnostic Decision (MDT Review)" : "Diagnostic Decision";
  const ssiAlwaysVisible = wf.isSSIAlwaysExpected;

  // One-clinician SLaM / 3 Borough (CI-first, PAD-now / defer wizard at the end
  // of the SSI Review): the same Path 1 flip as the SSI-first branch above, one
  // module along. When the Lead ran the PAD in the appointment straight after
  // the SSI, the overview reads CI → SSI → PAD → DD so the card order matches
  // what actually happened; Path 2 and undecided patients keep DD third. A
  // redo reverts to the canonical order for the same reason as above.
  const ssiPadInAppointment =
    wf.appointmentFinishPathModule === 'ssi' &&
    wf.ciFinishPath === 'padInAppointment' &&
    !opts.padRedone;

  if (ssiPadInAppointment) {
    return {
      moduleOrder: ['clinicalInterview', 'semiStructuredInterview', 'postAssessmentDiscussion', 'diagnosticDecision'],
      modules: {
        clinicalInterview: {
          title: "Clinical Interview",
          circleNumber: 1,
          alwaysVisible: true,
          disableWhenNotAdhd: false,
        },
        semiStructuredInterview: {
          title: "Semi-Structured Interview",
          circleNumber: 2,
          alwaysVisible: ssiAlwaysVisible,
          disableWhenNotAdhd: !ssiAlwaysVisible,
        },
        postAssessmentDiscussion: {
          title: "Post-Assessment Discussion",
          circleNumber: 3,
          alwaysVisible: ssiAlwaysVisible,
          disableWhenNotAdhd: !ssiAlwaysVisible,
        },
        diagnosticDecision: {
          title: ddTitle,
          circleNumber: 4,
          alwaysVisible: true,
          disableWhenNotAdhd: false,
        },
      },
    };
  }

  return {
    moduleOrder: ['clinicalInterview', 'semiStructuredInterview', 'diagnosticDecision', 'postAssessmentDiscussion'],
    modules: {
      clinicalInterview: {
        title: "Clinical Interview",
        circleNumber: 1,
        alwaysVisible: true,
        disableWhenNotAdhd: false,
      },
      semiStructuredInterview: {
        title: "Semi-Structured Interview",
        circleNumber: 2,
        alwaysVisible: ssiAlwaysVisible,
        disableWhenNotAdhd: !ssiAlwaysVisible,
      },
      diagnosticDecision: {
        title: ddTitle,
        circleNumber: 3,
        alwaysVisible: true,
        disableWhenNotAdhd: false,
      },
      postAssessmentDiscussion: {
        title: "Post-Assessment Discussion",
        circleNumber: 4,
        // PAD is always part of the workflow for always-SSI orgs (WLC, TOC,
        // A&A future) because a full assessment is always conducted — the DD
        // ADHD outcome only determines full vs partial PAD content, not
        // whether PAD happens.  PAD is skipped only for hierarchical +
        // whenAdhd orgs (Phoenix, Psych Consultations) where PPD ≠ ADHD
        // causes SSI to be skipped entirely.
        alwaysVisible: ssiAlwaysVisible,
        disableWhenNotAdhd: !ssiAlwaysVisible,
      },
    },
  };
};

/**
 * Derive the Patient Overview module layout for Adult Autism assessments.
 *
 * Differences from ADHD:
 * - History and Semi-Structured Interview are merged into one megablock card
 *   ("Diagnostic Developmental History") because launch orgs conduct both in
 *   a single back-to-back appointment. Per-org splitting can be reintroduced later.
 * - An "Observation" module sits after DDH and before DD/PAD (placeholder until
 *   the observation proforma ships)
 * - Module order and display titles (backend module keys unchanged):
 *   DDH > Behavioural Observation > MDT Review > Feedback Session
 *
 * @param {Object} wf - Resolved workflow config from useWorkflowConfig
 * @param {Object} [opts] - Same options as derivePatientOverviewConfig (e.g. padRedone)
 * @returns {Object} Overview config in the same shape as derivePatientOverviewConfig
 */
export const deriveAdultAutismPatientOverviewConfig = (wf, opts = {}) => {
  const adhdDerived = derivePatientOverviewConfig(wf, opts);

  // Behavioural Observation is optional: orgs whose workflow stamps
  // obsFlow.observation = disabled (e.g. Lancaster Medical Practice) do not run
  // a separate Observation appointment, so the card is dropped entirely and the
  // remaining cards renumber (DDH > MDT Review > Feedback Session). Absent flag
  // defaults to enabled, so The Owl Centre and every existing autism patient
  // keep the full four-card layout unchanged.
  const isObservationEnabled = wf?.isObservationEnabled !== false;

  // Circle numbers are assigned sequentially below so the visible order is
  // always 1..N with no gaps regardless of whether Observation is present.
  const behaviouralObservationModule = {
    title: 'Behavioural Observation',
    alwaysVisible: true,
    disableWhenNotAdhd: false,
  };

  const postAssessmentDiscussionModule = {
    ...adhdDerived.modules.postAssessmentDiscussion,
    title: 'Feedback Session',
    alwaysVisible: true,
    disableWhenNotAdhd: false,
  };

  const diagnosticDecisionModule = {
    ...adhdDerived.modules.diagnosticDecision,
    // Display title only — module key and backend stage remain diagnosticDecision.
    title: 'MDT Review',
  };

  // Single card covering History + SSI — both parts of one developmental
  // diagnostic interview appointment at launch orgs (e.g. The Owl Centre).
  const developmentalDiagnosticInterviewModule = {
    title: 'Diagnostic Developmental History',
    alwaysVisible: true,
    disableWhenNotAdhd: false,
    combinesModules: ['clinicalInterview', 'semiStructuredInterview'],
    isSSIFirst: Boolean(wf?.isSSIFirst),
  };

  const moduleOrder = [
    'developmentalDiagnosticInterview',
    ...(isObservationEnabled ? ['behaviouralObservation'] : []),
    'diagnosticDecision',
    'postAssessmentDiscussion',
  ];

  const modules = {
    developmentalDiagnosticInterview: developmentalDiagnosticInterviewModule,
    ...(isObservationEnabled ? { behaviouralObservation: behaviouralObservationModule } : {}),
    diagnosticDecision: diagnosticDecisionModule,
    postAssessmentDiscussion: postAssessmentDiscussionModule,
  };

  // Assign 1-based circle numbers in visible order so numbering has no gap when
  // Observation is omitted.
  moduleOrder.forEach((moduleKey, index) => {
    modules[moduleKey].circleNumber = index + 1;
  });

  return { moduleOrder, modules };
};

/**
 * Debug utility function to help troubleshoot configuration issues
 * Usage in console: debugPatientOverviewConfig('Phoenix Mental Health Services', false)
 */
export const debugPatientOverviewConfig = (organizationName, isAdhdFlow = true) => {
  const config = getPatientOverviewConfig(organizationName);
  
  console.group(`🔍 Patient Overview Config Debug - ${organizationName}`);
  console.log('Using config:', organizationName in overviewConfig ? organizationName : 'default (fallback)');
  console.log('Module Order:', config.moduleOrder);
  
  console.group('📋 Module Behaviour Analysis');
  config.moduleOrder.forEach(moduleKey => {
    const moduleConfig = config.modules[moduleKey];
    const wouldBeDisabled = !moduleConfig.alwaysVisible && moduleConfig.disableWhenNotAdhd && !isAdhdFlow;
    
    console.group(`${moduleConfig.title} (${moduleKey})`);
    console.log('Circle Number:', moduleConfig.circleNumber);
    console.log('Always Visible:', moduleConfig.alwaysVisible);
    console.log('Disable When Not ADHD:', moduleConfig.disableWhenNotAdhd);
    console.log(`Would be disabled with ADHD=${isAdhdFlow}:`, wouldBeDisabled);
    console.groupEnd();
  });
  console.groupEnd();
  
  console.groupEnd();
  
  return config;
};

/**
 * Validates a configuration object to ensure it has all required properties
 * Usage: validatePatientOverviewConfig(myConfig)
 */
export const validatePatientOverviewConfig = (config, configName = 'config') => {
  const errors = [];
  
  // Check moduleOrder exists and is array
  if (!config.moduleOrder || !Array.isArray(config.moduleOrder)) {
    errors.push(`${configName}: moduleOrder must be an array`);
  }
  
  // Check modules object exists
  if (!config.modules || typeof config.modules !== 'object') {
    errors.push(`${configName}: modules must be an object`);
  } else {
    // Check each module in moduleOrder has a corresponding config
    config.moduleOrder?.forEach(moduleKey => {
      if (!config.modules[moduleKey]) {
        errors.push(`${configName}: Missing module config for '${moduleKey}'`);
      } else {
        const module = config.modules[moduleKey];
        
        // Check required properties
        if (typeof module.title !== 'string') {
          errors.push(`${configName}.modules.${moduleKey}: title must be a string`);
        }
        if (typeof module.circleNumber !== 'number') {
          errors.push(`${configName}.modules.${moduleKey}: circleNumber must be a number`);
        }
        if (typeof module.alwaysVisible !== 'boolean') {
          errors.push(`${configName}.modules.${moduleKey}: alwaysVisible must be a boolean`);
        }
        if (typeof module.disableWhenNotAdhd !== 'boolean') {
          errors.push(`${configName}.modules.${moduleKey}: disableWhenNotAdhd must be a boolean`);
        }
      }
    });
  }
  
  if (errors.length > 0) {
    console.error(`❌ Configuration validation failed for ${configName}:`);
    errors.forEach(error => console.error(`  - ${error}`));
    return false;
  }
  
  // console.log(`✅ Configuration validation passed for ${configName}`);
  return true;
};

// Validate all configurations at module load (development mode only)
if (process.env.NODE_ENV === 'development') {
  Object.entries(overviewConfig).forEach(([orgName, config]) => {
    validatePatientOverviewConfig(config, orgName);
  });
}

/**
 * Configuration Schema Documentation:
 * 
 * moduleOrder: Array of module keys in display order
 * modules: Object containing configuration for each module
 *   - title: Display title for the module
 *   - circleNumber: Number displayed in the module card
 *   - alwaysVisible: If true, module is never disabled due to ADHD diagnosis
 *   - disableWhenNotAdhd: If true, module is disabled when provisional diagnosis is not ADHD
 * 
 * To add a new organization:
 * 1. Create a new config object extending defaultConfig
 * 2. Override specific module properties as needed
 * 3. Add the organization name mapping in overviewConfig
 * 
 * Example:
 * const newOrgConfig = {
 *   ...defaultConfig,
 *   modules: {
 *     ...defaultConfig.modules,
 *     semiStructuredInterview: {
 *       ...defaultConfig.modules.semiStructuredInterview,
 *       disableWhenNotAdhd: false,
 *       title: "Custom SSI Title"
 *     }
 *   }
 * };
 */